import { ChangeDetectionStrategy, Component, OnDestroy, signal } from '@angular/core';

import { environment } from '../../../environments/environment';

/** How often to re-probe while the banner is showing. */
export const OFFLINE_REPROBE_MS = 10_000;

/** Offline banner (rewritten 2026-09-16, prod incident 2026-09-14).
 *
 *  `navigator.onLine` alone is not evidence. Chrome on macOS derives it from
 *  the default route's reachability, and behind a corporate tunnel it reports
 *  offline while every request succeeds; a BEP user saw this banner for two
 *  weeks with working internet. The banner now shows only after a real probe
 *  to the API fails with a network error. Any HTTP status, including a 503,
 *  means the network is up. While shown it re-probes every ten seconds and on
 *  the browser's `online` event, and clears on the first success.
 *
 *  Position is fixed, like the update banner: as an in-flow block it pushed the
 *  100vh app shell down and hung the composer and profile button off screen. */
@Component({
    selector: 'app-offline-banner',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (offline()) {
            <div class="offline-banner" data-testid="offline-banner" role="status">
                <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M1.5 1.5l13 13M3.7 7.7a6 6 0 0 1 2.1-1.5M12.3 7.7a6 6 0 0 0-1.1-.8M5.8 9.8a4 4 0 0 1 4.4 0M8 12a1 1 0 1 1 0 .01"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.5" />
                </svg>
                <span>You're offline. Some features may be unavailable until you reconnect.</span>
            </div>
        }
    `,
    styles: [
        `
            .offline-banner {
                position: fixed;
                top: 0.75rem;
                left: 50%;
                transform: translateX(-50%);
                z-index: 900;
                display: flex;
                align-items: center;
                gap: 0.5rem;
                max-width: calc(100vw - 2rem);
                padding: 0.5rem 1rem;
                border-radius: 0.5rem;
                background: var(--wa-color-warning-container, #fef3c7);
                color: var(--wa-color-warning-on-container, #92400e);
                border: 1px solid var(--wa-color-warning-outline, #f59e0b);
                box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
                font-size: 0.8125rem;
                line-height: 1.25rem;
            }
            .offline-banner svg {
                flex-shrink: 0;
            }
        `,
    ],
})
export class OfflineBanner implements OnDestroy {
    readonly offline = signal(false);

    /** Resolves true when the API is reachable (any HTTP status), false on a
     *  network error. Overridable for tests. */
    probe: () => Promise<boolean> = async () => {
        try {
            await fetch(`${environment.apiBase}/hello`, { cache: 'no-store', method: 'GET' });
            return true;
        } catch {
            return false;
        }
    };

    private timer: ReturnType<typeof setInterval> | null = null;
    private probing = false;

    private readonly onOffline = (): void => {
        void this.check();
    };
    private readonly onOnline = (): void => {
        void this.check();
    };

    constructor() {
        window.addEventListener('offline', this.onOffline);
        window.addEventListener('online', this.onOnline);
        if (!navigator.onLine) {
            void this.check();
        }
    }

    /** Probe once and set the banner from the result. Starts the re-probe
     *  timer when going offline, stops it when back. */
    async check(): Promise<void> {
        if (this.probing) {
            return;
        }
        this.probing = true;
        try {
            const reachable = await this.probe();
            this.offline.set(!reachable);
            if (reachable) {
                this.stopTimer();
            } else if (this.timer === null) {
                this.timer = setInterval(() => void this.check(), OFFLINE_REPROBE_MS);
            }
        } finally {
            this.probing = false;
        }
    }

    private stopTimer(): void {
        if (this.timer !== null) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    ngOnDestroy(): void {
        window.removeEventListener('offline', this.onOffline);
        window.removeEventListener('online', this.onOnline);
        this.stopTimer();
    }
}
